import React, { useState } from 'react';
import adminService from '../../services/api/admin';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { getArabicDate } from '../../services/utils/date';
import { 
  Users, 
  Calendar, 
  TrendingUp, 
  Search, 
  XCircle, 
  RefreshCw, 
  Eye, 
  ShieldCheck, 
  Layers, 
  DollarSign, 
  Clock, 
  ChevronLeft, 
  ChevronRight,
  UserCheck,
  CheckCircle2,
  X,
  FileText,
  Download
} from 'lucide-react';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { useToast } from '../../components/common/Toast';
import { getImageUrl } from '../../config/apiConfig';

const SubscriptionsManagement = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedPeriod, setSelectedPeriod] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const queryClient = useQueryClient();
  const toast = useToast();

  // جلب الاشتراكات (المنطق الأصلي كاملاً)
  const { data: subscriptions, isLoading, error } = useQuery(
  ['subscriptions', { searchTerm, selectedStatus, selectedPeriod, currentPage }],
  () =>
    adminService.getSubscriptions({
      search: searchTerm,
      status: selectedStatus,
      period: selectedPeriod,
      page: currentPage,
      limit: 10
    }),
  { keepPreviousData: true }
);


  // تحديث حالة الاشتراكات (Mutation)
  const updateStatusMutation = useMutation(
    async ({ subscriptionId, status }) => {
      const response = await adminService.updateSubscriptionStatus(subscriptionId, { status });
      return response.data;
    },
    {
      onSuccess: () => {
        toast.success('تم تحديث حالة الاشتراك بنجاح');
        queryClient.invalidateQueries(['subscriptions']);
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'فشل تحديث حالة الاشتراك');
      }
    }
  );

  // تصدير البيانات (Mutation)
  const exportMutation = useMutation(
    async (filters) => {
      const response = await adminService.exportSubscriptions(filters);
      return response.data;
    },
    {
      onSuccess: (data) => {
        const link = document.createElement('a');
        link.href = data.exportUrl;
        link.download = `subscriptions-report-${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success('تم تجهيز وتحميل ملف البيانات بنجاح');
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'فشل تصدير البيانات');
      }
    }
  );

  const handleStatusUpdate = (subscriptionId, currentStatus) => {
    const newStatus = currentStatus === 'active' ? 'cancelled' : 'active';
    updateStatusMutation.mutate({ subscriptionId, status: newStatus });
  };

  const handleShowDetails = (subscription) => {
    setSelectedSubscription(subscription);
    setShowDetailsModal(true);
  };

  const handleExport = () => {
    exportMutation.mutate({
      status: selectedStatus,
      period: selectedPeriod,
      search: searchTerm
    });
  };

  const getStatusBadgeStyle = (status) => {
    switch (status) {
      case 'active': return 'bg-emerald-50 text-emerald-700 border-emerald-100';
      case 'expired': return 'bg-rose-50 text-rose-700 border-rose-100';
      case 'cancelled': return 'bg-gray-100 text-gray-700 border-gray-200';
      case 'pending': return 'bg-amber-50 text-amber-700 border-amber-100';
      default: return 'bg-gray-50 text-gray-600 border-gray-100';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'active': return 'نشط ومفعل';
      case 'expired': return 'منتهي الصلاحية';
      case 'cancelled': return 'ملغي إدارياً';
      case 'pending': return 'بانتظار التأكيد';
      default: return status;
    }
  };

  // دوال الحساب (المنطق الأصلي كاملاً)
  const calculateRevenue = (subs) => {
    const revenue = subs?.filter(sub => (sub.calculated_status || sub.status) === 'active')?.reduce((total, sub) => {
      const price = parseFloat(sub.price) || 0;
      return total + price;
    }, 0) || 0;
    return revenue.toLocaleString('en-US');
  };

  const calculateGrowth = (subs) => {
    const thisMonth = new Date().getMonth();
    const thisYear = new Date().getFullYear();
    const thisMonthCount = subs?.filter(sub => {
      const d = new Date(sub.created_at);
      return d.getMonth() === thisMonth && d.getFullYear() === thisYear;
    }).length || 0;
    const lastMonth = thisMonth === 0 ? 11 : thisMonth - 1;
    const lastMonthCount = subs?.filter(sub => {
      const d = new Date(sub.created_at);
      return d.getMonth() === lastMonth && d.getFullYear() === thisYear;
    }).length || 0;
    return thisMonthCount - lastMonthCount;
  };

  if (isLoading && !subscriptions) {
    return (
      <div className="flex flex-col items-center justify-center py-24 min-h-[500px]">
        <LoadingSpinner size="large" />
        <p className="mt-4 text-gray-500 font-bold animate-pulse uppercase tracking-widest text-xs">جاري تحميل سجل الاشتراكات...</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-10 px-4 sm:px-6 py-6 min-h-screen bg-gray-50/30">
      
      {/* Header Section */}
      <div className="bg-white rounded-[2.5rem] shadow-sm border border-gray-100 p-8 lg:p-10 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-32 h-32 bg-primary-50 rounded-br-[5rem] opacity-40"></div>
        <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="space-y-1">
            <h1 className="text-3xl lg:text-4xl font-black text-gray-900 tracking-tight flex items-center gap-3">
                <ShieldCheck className="text-primary-600" size={32} />
                إدارة كافة الاشتراكات
            </h1>
            <p className="text-gray-500 font-medium flex items-center gap-2">
                <Layers size={18} className="text-emerald-500" />
                متابعة التدفقات المالية، حالات الطلاب، وتقارير النمو الدوري للمنصة
            </p>
          </div>
          <button
            onClick={handleExport}
            disabled={exportMutation.isLoading}
            className="bg-emerald-600 text-white px-8 py-4 rounded-2xl font-black text-sm hover:bg-emerald-700 transition-all shadow-xl shadow-emerald-100 flex items-center gap-2 transform active:scale-95 disabled:opacity-50"
          >
            {exportMutation.isLoading ? <RefreshCw className="animate-spin" size={20} /> : <Download size={20} />}
            تصدير ملف CSV
          </button>
        </div>
      </div>

      {/* Financial Stats Dashboard */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {[
          { label: 'إجمالي المشتركين', value: subscriptions?.pagination?.total || 0, icon: Users, color: 'text-blue-600', bg: 'bg-blue-50' },
          { label: 'إيرادات الاشتراكات النشطة', value: `${calculateRevenue(subscriptions?.subscriptions)} ج.م`, icon: DollarSign, color: 'text-emerald-600', bg: 'bg-emerald-50' },
          { label: 'الاشتراكات النشطة حالياً', value: subscriptions?.subscriptions?.filter(s => (s.calculated_status || s.status) === 'active').length || 0, icon: UserCheck, color: 'text-purple-600', bg: 'bg-purple-50' },
          { label: 'معدل النمو الشهري', value: calculateGrowth(subscriptions?.subscriptions), icon: TrendingUp, color: calculateGrowth(subscriptions?.subscriptions) >= 0 ? 'text-green-600' : 'text-rose-600', bg: calculateGrowth(subscriptions?.subscriptions) >= 0 ? 'bg-green-50' : 'bg-rose-50', isGrowth: true }
        ].map((stat, i) => (
          <div key={i} className="bg-white p-6 rounded-[2rem] border border-gray-100 shadow-sm flex items-center gap-5 group hover:shadow-md transition-all">
            <div className={`p-4 ${stat.bg} ${stat.color} rounded-2xl group-hover:scale-110 transition-transform`}>
              <stat.icon size={24} />
            </div>
            <div>
              <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest leading-none mb-1">{stat.label}</p>
              <p className="text-2xl font-black text-gray-900 leading-none">
                  {stat.isGrowth && stat.value > 0 ? '+' : ''}{stat.value}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* Advanced Filters Toolbar */}
      <div className="bg-white rounded-[2rem] shadow-sm border border-gray-100 p-6">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-center">
          
          <div className="relative md:col-span-6 group">
            <Search className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-primary-500 transition-colors w-5 h-5" />
            <input
              type="text"
              placeholder="ابحث عن اشتراك (اسم الطالب، البريد، أو الكورس)..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pr-12 pl-4 py-4 bg-gray-50 border border-transparent rounded-2xl focus:bg-white focus:ring-4 focus:ring-primary-500/10 focus:border-primary-500 transition-all font-medium"
            />
          </div>
          
          <div className="md:col-span-3">
            <div className="relative">
                <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="w-full px-5 py-4 bg-gray-50 border border-gray-100 rounded-2xl focus:ring-4 focus:ring-primary-500/10 focus:bg-white transition-all font-bold text-gray-700 appearance-none cursor-pointer"
                >
                <option value="">جميع حالات الاشتراك</option>
                <option value="active">نشط ومفعل</option>
                <option value="expired">منتهي</option>
                <option value="cancelled">ملغي</option>
                <option value="pending">في الانتظار</option>
                </select>
                <ChevronLeft className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 -rotate-90" size={16} />
            </div>
          </div>
          
          <div className="md:col-span-3">
            <div className="relative">
                <select
                value={selectedPeriod}
                onChange={(e) => setSelectedPeriod(e.target.value)}
                className="w-full px-5 py-4 bg-gray-50 border border-gray-100 rounded-2xl focus:ring-4 focus:ring-primary-500/10 focus:bg-white transition-all font-bold text-gray-700 appearance-none cursor-pointer"
                >
                <option value="">جميع الفترات الزمنية</option>
                <option value="today">طلبات اليوم</option>
                <option value="week">هذا الأسبوع</option>
                <option value="month">هذا الشهر</option>
                <option value="year">هذا العام</option>
                </select>
                <ChevronLeft className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 -rotate-90" size={16} />
            </div>
          </div>
        </div>
      </div>

      {/* Main Subscriptions Grid Table */}
      <div className="bg-white rounded-[2.5rem] shadow-sm border border-gray-100 overflow-hidden group transition-all duration-500 hover:shadow-xl">
        {error ? (
          <div className="py-24 text-center">
            <XCircle className="w-16 h-16 text-rose-500 mx-auto mb-4" />
            <p className="text-rose-600 font-black text-lg">حدث خطأ أثناء جلب سجلات الاشتراكات</p>
          </div>
        ) : subscriptions?.subscriptions?.length === 0 ? (
            <div className="py-24 text-center">
                <div className="w-24 h-24 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-6">
                    <FileText size={48} className="text-gray-200" />
                </div>
                <h3 className="text-2xl font-black text-gray-900 mb-2">لا توجد سجلات مطابقة</h3>
                <p className="text-gray-500 max-w-sm mx-auto font-medium">لم نتمكن من العثور على أي اشتراكات تطابق معايير البحث أو الفلترة الحالية.</p>
            </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-50">
              <thead className="bg-gray-50/50">
                <tr>
                  <th className="px-8 py-6 text-right text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">بيانات الطالب</th>
                  <th className="px-8 py-6 text-right text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">الكورس المشترك</th>
                  <th className="px-8 py-6 text-right text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">التكلفة</th>
                  <th className="px-8 py-6 text-right text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">المدة الزمنية</th>
                  <th className="px-8 py-6 text-right text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">الحالة</th>
                  <th className="px-8 py-6 text-left text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">الإجراءات</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-50">
                {subscriptions?.subscriptions?.map((subscription) => (
                  <tr key={subscription.id} className="hover:bg-primary-50/30 transition-colors group/row">
                    <td className="px-8 py-6 whitespace-nowrap">
                      <div className="flex items-center gap-4">
                        <div className="relative">
                            <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-primary-700 rounded-2xl flex items-center justify-center text-white font-black shadow-lg shadow-primary-100 group-hover/row:scale-110 transition-transform overflow-hidden">
                                {subscription.student_avatar ? (
                                  <img src={getImageUrl(subscription.student_avatar)} alt="" className="w-full h-full object-cover" />
                                ) : (
                                    <span className="text-xl">{subscription.student_name?.charAt(0).toUpperCase()}</span>
                                )}
                            </div>
                        </div>
                        <div>
                          <div className="text-base font-black text-gray-900 group-hover/row:text-primary-600 transition-colors">{subscription.student_name}</div>
                          <div className="text-[10px] font-bold text-gray-400 uppercase tracking-tighter">{subscription.student_email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-8 py-6 whitespace-nowrap">
                      <div className="max-w-xs truncate text-sm font-black text-gray-700 bg-gray-50 px-3 py-1.5 rounded-xl border border-gray-100">
                        {subscription.course_title || `${subscription.subject_name} - ${subscription.grade_name}` || 'غير محدد'}
                      </div>
                    </td>
                    <td className="px-8 py-6 whitespace-nowrap">
                      <div className="flex flex-col">
                        <span className="text-sm font-black text-gray-900">{subscription.price || 0} ج.م</span>
                        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-tighter">مدفوع بالكامل</span>
                      </div>
                    </td>
                    <td className="px-8 py-6 whitespace-nowrap">
                      <div className="space-y-1.5">
                        <div className="flex items-center gap-2 text-[10px] font-black text-gray-500 uppercase tracking-tighter">
                            <Calendar size={12} className="text-emerald-500" />
                            بدأ: {getArabicDate(subscription.start_date)}
                        </div>
                        <div className="flex items-center gap-2 text-[10px] font-black text-gray-500 uppercase tracking-tighter">
                            <Clock size={12} className="text-rose-500" />
                            ينتهي: {getArabicDate(subscription.end_date)}
                        </div>
                      </div>
                    </td>
                    <td className="px-8 py-6 whitespace-nowrap">
                      <span className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-xl text-[10px] font-black uppercase border tracking-widest ${getStatusBadgeStyle(subscription.calculated_status || subscription.status)}`}>
                        {getStatusLabel(subscription.calculated_status || subscription.status)}
                      </span>
                    </td>
                    <td className="px-8 py-6 whitespace-nowrap text-left">
                      <div className="flex items-center justify-end gap-3 opacity-0 group-hover/row:opacity-100 transition-opacity">
                        <button
                          onClick={() => handleStatusUpdate(subscription.id, subscription.calculated_status || subscription.status)}
                          className={`w-10 h-10 flex items-center justify-center rounded-xl transition-all shadow-sm border ${
                            (subscription.calculated_status || subscription.status) === 'active' 
                              ? 'bg-rose-50 text-rose-600 border-rose-100 hover:bg-rose-600 hover:text-white' 
                              : 'bg-emerald-50 text-emerald-600 border-emerald-100 hover:bg-emerald-600 hover:text-white'
                          }`}
                          title={(subscription.calculated_status || subscription.status) === 'active' ? 'إلغاء الاشتراك' : 'تنشيط الاشتراك'}
                        >
                          {(subscription.calculated_status || subscription.status) === 'active' ? <X size={18} /> : <CheckCircle2 size={18} />}
                        </button>
                        
                        <button 
                          onClick={() => handleShowDetails(subscription)}
                          className="w-10 h-10 flex items-center justify-center bg-gray-50 text-gray-400 hover:bg-gray-900 hover:text-white rounded-xl transition-all shadow-sm border border-gray-100" 
                          title="تفاصيل الاشتراك"
                        >
                          <Eye size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modern Pagination Section */}
      {subscriptions?.pagination && (
        <div className="bg-white rounded-[2rem] shadow-sm p-6 border border-gray-100 flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="text-sm font-bold text-gray-500">
              عرض <span className="text-primary-600">{subscriptions?.pagination?.from}</span> إلى <span className="text-primary-600">{subscriptions?.pagination?.to}</span> من <span className="font-black text-gray-900">{subscriptions?.pagination?.total}</span> اشتراك مسجل
            </div>
            
            <div className="flex items-center gap-3">
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="p-3 rounded-xl border border-gray-100 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-gray-50 transition-all text-primary-600"
              >
                <ChevronRight size={20} />
              </button>
              
              <div className="flex items-center gap-1.5">
                  <span className="bg-primary-600 text-white w-10 h-10 flex items-center justify-center rounded-xl font-black text-sm shadow-lg shadow-primary-100">
                    {currentPage}
                  </span>
                  <span className="text-gray-400 px-1 font-bold text-xs">من</span>
                  <span className="bg-gray-100 text-gray-600 w-10 h-10 flex items-center justify-center rounded-xl font-black text-sm">
                    {subscriptions?.pagination?.lastPage}
                  </span>
              </div>

              <button
                onClick={() => setCurrentPage(prev => Math.min(subscriptions?.pagination?.lastPage, prev + 1))}
                disabled={currentPage === subscriptions?.pagination?.lastPage}
                className="p-3 rounded-xl border border-gray-100 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-gray-50 transition-all text-primary-600"
              >
                <ChevronLeft size={20} />
              </button>
            </div>
        </div>
      )}
      
      {/* Subscription Details Modal */}
      {showDetailsModal && selectedSubscription && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-[2rem] shadow-2xl max-w-4xl w-full max-h-[70vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-primary-600 to-primary-700 text-white p-8 rounded-t-[2rem] relative">
              <button
                onClick={() => setShowDetailsModal(false)}
                className="absolute top-6 left-6 w-10 h-10 bg-white/20 hover:bg-white/30 rounded-xl flex items-center justify-center transition-all"
              >
                <X size={20} />
              </button>
              <h2 className="text-2xl font-black mb-2">تفاصيل الاشتراك</h2>
              <p className="text-primary-100 text-sm">كافة المعلومات المتعلقة بالاشتراك</p>
            </div>

            {/* Modal Content */}
            <div className="p-8 space-y-6">
              {/* Student and Course Information Side by Side */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Student Information */}
                <div className="bg-gray-50 rounded-2xl p-6">
                  <h3 className="text-lg font-black text-gray-900 mb-4 flex items-center gap-2">
                    <Users size={20} className="text-primary-600" />
                    بيانات الطالب
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <p className="text-sm text-gray-500 mb-1">اسم الطالب</p>
                      <p className="font-black text-gray-900">{selectedSubscription.student_name}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 mb-1">البريد الإلكتروني</p>
                      <p className="font-black text-gray-900">{selectedSubscription.student_email}</p>
                    </div>
                  </div>
                </div>

                {/* Course Information */}
                <div className="bg-gray-50 rounded-2xl p-6">
                  <h3 className="text-lg font-black text-gray-900 mb-4 flex items-center gap-2">
                    <FileText size={20} className="text-primary-600" />
                    معلومات الكورس
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <p className="text-sm text-gray-500 mb-1">اسم الكورس</p>
                      <p className="font-black text-gray-900">
                        {selectedSubscription.course_title || `${selectedSubscription.subject_name} - ${selectedSubscription.grade_name}`}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 mb-1">المادة</p>
                      <p className="font-black text-gray-900">{selectedSubscription.subject_name}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 mb-1">الصف الدراسي</p>
                      <p className="font-black text-gray-900">{selectedSubscription.grade_name}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 mb-1">مدرس المادة</p>
                      <p className="font-black text-gray-900">{selectedSubscription.teacher_name}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Subscription Information */}
              <div className="bg-gray-50 rounded-2xl p-6">
                <h3 className="text-lg font-black text-gray-900 mb-4 flex items-center gap-2">
                  <Calendar size={20} className="text-primary-600" />
                  معلومات الاشتراك
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div>
                    <p className="text-sm text-gray-500 mb-1">التكلفة</p>
                    <p className="font-black text-gray-900">{selectedSubscription.price || 0} ج.م</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 mb-1">حالة الاشتراك</p>
                    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-black uppercase border tracking-widest ${getStatusBadgeStyle(selectedSubscription.calculated_status || selectedSubscription.status)}`}>
                      {getStatusLabel(selectedSubscription.calculated_status || selectedSubscription.status)}
                    </span>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 mb-1">تاريخ البدء</p>
                    <p className="font-black text-gray-900">{getArabicDate(selectedSubscription.start_date)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 mb-1">تاريخ الانتهاء</p>
                    <p className="font-black text-gray-900">{getArabicDate(selectedSubscription.end_date)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 mb-1">نوع الاشتراك</p>
                    <p className="font-black text-gray-900">
                      {selectedSubscription.subscription_type === 'manual_payment' ? 'دفع يدوي' : 'كود تفعيل'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 mb-1">رقم المعاملة</p>
                    <p className="font-black text-gray-900">{selectedSubscription.payment_id || 'غير متوفر'}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="bg-gray-50 px-8 py-6 rounded-b-[2rem] flex justify-end gap-4">
              <button
                onClick={() => setShowDetailsModal(false)}
                className="px-6 py-3 bg-gray-200 text-gray-700 rounded-xl font-black hover:bg-gray-300 transition-all"
              >
                إغلاق
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionsManagement;